import {
  FONT_WEIGHT_OPTIONS,
  GOOGLE_FONTS_SET,
} from "replo-runtime/shared/utils/font";
import { GOOGLE_FONTS_METADATA } from "schemas/generated/googleFontsMetadata";

export interface FontWeightOption {
  label: string;
  value: string;
}

/**
 * Normalizes a font family string by extracting the first font name
 * and removing quotes and whitespace.
 * @param fontFamily - The font family string to normalize (e.g., "'Roboto', sans-serif")
 * @returns The normalized font name (e.g., "Roboto")
 */
export function normalizeFontFamily(fontFamily: string): string {
  return fontFamily.split(",")[0]!.trim().replace(/["']/g, "");
}

/**
 * Returns the available font weights for a given font family.
 * Checks project fonts first, then Google Fonts metadata, then falls back to all standard weights.
 * @param fontFamily - The font family to get weights for
 * @param projectFonts - Array of project fonts with name and weight
 * @returns Array of available weights sorted ascending
 */
export function getAvailableWeights(
  fontFamily: string,
  projectFonts: { name: string; weight: string | null }[],
): number[] {
  const normalizedFamily = normalizeFontFamily(fontFamily);

  const matchingProjectFonts = projectFonts.filter(
    (font) => font.name === normalizedFamily,
  );

  if (matchingProjectFonts.length > 0) {
    const projectWeights = new Set<number>();
    for (const font of matchingProjectFonts) {
      if (font.weight) {
        projectWeights.add(Number(font.weight));
      }
    }
    if (projectWeights.size > 0) {
      return Array.from(projectWeights).sort((a, b) => a - b);
    }
  }

  if (GOOGLE_FONTS_SET.has(normalizedFamily)) {
    const metadata = GOOGLE_FONTS_METADATA[normalizedFamily];
    if (metadata?.weights) {
      return metadata.weights;
    }
  }

  return FONT_WEIGHT_OPTIONS.map((opt) => opt.value);
}

/**
 * NOTE (Kevin, 2026-01-10, REPL-19696)
 * @description Find the closest available weight to the target weight.
 * @param availableWeights - Array of available weights
 * @param targetWeight - The target weight to find the closest match for
 * @returns The closest available weight, or 400 if no weights available
 */
export function findClosestWeight(
  availableWeights: number[],
  targetWeight: number,
): number {
  if (availableWeights.length === 0) {
    return 400;
  }

  return availableWeights.reduce((closest, weight) => {
    return Math.abs(weight - targetWeight) < Math.abs(closest - targetWeight)
      ? weight
      : closest;
  });
}

export const findByWeight = (options: FontWeightOption[], weight: number) => {
  return options.find((opt) => Number(opt.value) === weight);
};
